import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ShoppingBag, Filter, Loader2, List, LayoutGrid } from 'lucide-react';
import { getCategoryIcon } from '@/lib/categoryIcons';
import { Button } from '@/components/ui/button';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useProducts } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { useLanguage } from '@/hooks/useLanguage';

import { SEO, getBreadcrumbSchema } from '@/hooks/useSEO';
import { getTranslatedField } from '@/lib/translationHelpers';
import ProductGridCard from '@/components/shop/ProductGridCard';
import ProductListCard from '@/components/shop/ProductListCard';
import { CountryFilter } from '@/components/CountryFilter';
import { SortSelect } from '@/components/SortSelect';
import { sortProducts, type SortBy } from '@/lib/sortProducts';

const updateFrequencyLabels: Record<string, string> = {
  weekly: 'shop.frequency.weekly',
  monthly: 'shop.frequency.monthly',
  quarterly: 'shop.frequency.quarterly',
  yearly: 'shop.frequency.yearly',
};

export default function ShopPage() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: dbCategories = [] } = useCategories();
  
  const categoryFromUrl = searchParams.get('category');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    categoryFromUrl || 'all'
  );
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortBy>('sku');

  const { data: products, isLoading, error } = useProducts(
    selectedCategory === 'all' ? undefined : selectedCategory
  );

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    if (category === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', category);
    }
    setSearchParams(searchParams);
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="container py-12 lg:py-16">
      <SEO
        title={t('shop.title')}
        description={t('meta.shop')}
        path="/shop"
        jsonLd={getBreadcrumbSchema([
          { name: t('common.home'), url: `https://postnummerservice.se/${lang}` },
          { name: t('shop.title'), url: `https://postnummerservice.se/${lang}/shop` },
        ])}
      />
      {/* Header */}
      <section className="mb-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-secondary/10 flex items-center justify-center flex-shrink-0">
            <ShoppingBag className="w-6 h-6 text-secondary" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              {t('shop.title')}
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              {t('shop.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium">{t('shop.filterBy')}</span>
          </div>
          <div className="flex items-center gap-3">
            <SortSelect value={sortBy} onChange={setSortBy} />
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(val) => { if (val) setViewMode(val as 'list' | 'grid'); }}
              size="sm"
            >
              <ToggleGroupItem value="list" aria-label="List view">
                <List className="w-4 h-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="grid" aria-label="Grid view">
                <LayoutGrid className="w-4 h-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleCategoryChange('all')}
            className="flex items-center gap-2"
          >
            <ShoppingBag className="w-4 h-4" />
            {t('shop.allCategories')}
          </Button>
          {dbCategories.map((cat) => {
            const Icon = getCategoryIcon(cat.icon);
            const isSelected = selectedCategory === cat.slug;
            return (
              <Button
                key={cat.id}
                variant={isSelected ? 'default' : 'outline'}
                size="sm"
                onClick={() => handleCategoryChange(cat.slug)}
                className="flex items-center gap-2"
              >
                <Icon className="w-4 h-4" />
                {cat.name}
              </Button>
            );
          })}
        </div>
        <CountryFilter
          selected={selectedCountries}
          onChange={setSelectedCountries}
          className="mt-3"
        />
      </section>

      {/* Products */}
      <section>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-destructive">
            {t('shop.errorLoading')}
          </div>
        ) : products && products.length > 0 ? (
          (() => {
            const filteredProducts = sortProducts(
              products.filter((p) =>
                selectedCountries.length === 0 ||
                p.available_countries?.some((c) => selectedCountries.includes(c))
              ),
              sortBy
            );
            if (filteredProducts.length === 0) {
              return (
                <div className="text-center py-12 text-muted-foreground">
                  {t('shop.noProducts')}
                </div>
              );
            }
            return viewMode === 'grid' ? (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredProducts.map((product) => {
                  const catObj = dbCategories.find((c) => c.slug === product.ws_category);
                  return (
                    <ProductGridCard
                      key={product.id}
                      product={product}
                      categoryIcon={getCategoryIcon(catObj?.icon ?? null)}
                      categoryLabel={catObj ? getTranslatedField(catObj, 'name', lang) : product.ws_category}
                      lang={lang}
                      formatPrice={formatPrice}
                      updateFrequencyLabels={updateFrequencyLabels}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {filteredProducts.map((product) => {
                  const catObj = dbCategories.find((c) => c.slug === product.ws_category);
                  return (
                    <ProductListCard
                      key={product.id}
                      product={product}
                      categoryIcon={getCategoryIcon(catObj?.icon ?? null)}
                      categoryLabel={catObj ? getTranslatedField(catObj, 'name', lang) : product.ws_category}
                      lang={lang}
                      formatPrice={formatPrice}
                      updateFrequencyLabels={updateFrequencyLabels}
                    />
                  );
                })}
              </div>
            );
          })()
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            {t('shop.noProducts')}
          </div>
        )}
      </section>
    </div>
  );
}
